import React from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import type { CatalogModel } from './hooks/useAICatalog';

/** "163k" style token counts. */
export const formatTokens = (tokens: number): string =>
  tokens >= 1000 ? `${Math.round(tokens / 1000)}k` : String(tokens);

/** Below this share of the window there is nothing worth compacting. */
const MIN_COMPACT_FRACTION = 0.1;

interface ContextMeterProps {
  contextTokens: number;
  model: CatalogModel;
  compactionCount: number;
  /** The viewer owns the chat (only owners can compact). */
  canAct: boolean;
  /** A turn or compaction is in progress. */
  busy: boolean;
  compacting: boolean;
  onCompact: () => void;
}

/**
 * How full the chat's model context is, with the automatic-compaction point
 * marked, and the owner's "Compact now".
 */
const ContextMeter: React.FC<ContextMeterProps> = ({
  contextTokens,
  model,
  compactionCount,
  canAct,
  busy,
  compacting,
  onCompact,
}) => {
  const fill = Math.min(1, contextTokens / model.maxInputTokens);
  const trigger = model.compactTriggerTokens / model.maxInputTokens;
  const enough = contextTokens >= model.maxInputTokens * MIN_COMPACT_FRACTION;
  const tooltip =
    `~${formatTokens(contextTokens)} of ${formatTokens(model.maxInputTokens)} tokens in context · ` +
    `compacts automatically at ~${formatTokens(model.compactTriggerTokens)}` +
    (compactionCount ? ` · compacted ${compactionCount}×` : '');
  return (
    <div
      className="d-flex align-items-center small text-muted gap-2 px-3 pt-2"
      data-testid="chat-context-meter"
      title={tooltip}
    >
      <span>Context</span>
      <div
        className="position-relative flex-grow-1 rounded"
        style={{ height: 6, background: 'rgba(128,128,128,0.25)' }}
        role="meter"
        aria-label="Chat context used"
        aria-valuemin={0}
        aria-valuemax={model.maxInputTokens}
        aria-valuenow={contextTokens}
      >
        <div
          className="h-100 rounded"
          style={{
            width: `${fill * 100}%`,
            background: fill >= trigger ? '#d9822b' : '#3b82f6',
          }}
        />
        <div
          className="position-absolute top-0 h-100"
          style={{
            left: `${trigger * 100}%`,
            width: 2,
            background: 'rgba(255,255,255,0.7)',
          }}
        />
      </div>
      <span>
        {formatTokens(contextTokens)} / {formatTokens(model.maxInputTokens)}
      </span>
      {canAct && (
        <StylishNewButton
          className="button--secondary"
          type="button"
          onClick={onCompact}
          disabled={busy || compacting || !enough}
          title={
            enough
              ? 'Summarize the earlier conversation now to free up context'
              : 'Nothing to compact yet'
          }
          data-testid="chat-compact-now"
        >
          {compacting ? (
            <>
              Compacting&nbsp;<i className="fa fa-spinner fa-pulse"></i>
            </>
          ) : (
            'Compact now'
          )}
        </StylishNewButton>
      )}
    </div>
  );
};

export default ContextMeter;
